import { useCallback, useMemo, useState } from 'react';
import { ActionIcon, Popover, Radio, Select, Stack, Text, Tooltip } from '@mantine/core';
import { IconSettings } from '@tabler/icons-react';
import type { QuestionnaireItem } from '@medplum/fhirtypes';

import {
  getConditionalRule,
  getTriggerAnswerOptions,
  isConditionalSiblingItem,
  listPriorSiblingTriggers,
  setConditionalRule,
} from '../utils/conditionalQuestions';

const INNER_INPUT_BG = { backgroundColor: 'var(--mantine-color-gray-0)' };

interface ConditionalVisibilityGearProps {
  item: QuestionnaireItem;
  siblings: QuestionnaireItem[];
  siblingIndex: number;
  onUpdate: (update: Partial<QuestionnaireItem>) => void;
}

function VisibilitySettings({
  item,
  siblings,
  siblingIndex,
  onUpdate,
}: ConditionalVisibilityGearProps): JSX.Element {
  const priorTriggers = useMemo(
    () => listPriorSiblingTriggers(siblings, siblingIndex),
    [siblings, siblingIndex],
  );

  const rule = getConditionalRule(item);
  const mode = rule ? 'conditional' : 'always';

  const selectedTrigger = useMemo(
    () => priorTriggers.find((t) => t.linkId === rule?.triggerLinkId),
    [priorTriggers, rule?.triggerLinkId],
  );

  const answerOptions = useMemo(
    () => (selectedTrigger ? getTriggerAnswerOptions(selectedTrigger) : []),
    [selectedTrigger],
  );

  const applyRule = useCallback(
    (trigger: QuestionnaireItem | undefined, answerKey: string | null) => {
      const next = setConditionalRule(item, trigger, answerKey);
      if (next.enableWhen?.length) {
        onUpdate({ enableWhen: next.enableWhen, enableBehavior: next.enableBehavior });
      } else {
        onUpdate({ enableWhen: undefined, enableBehavior: undefined });
      }
    },
    [item, onUpdate],
  );

  const handleModeChange = useCallback(
    (next: string) => {
      if (next === 'always') {
        applyRule(undefined, null);
        return;
      }
      const firstTrigger = priorTriggers[0];
      if (!firstTrigger) return;
      const answers = getTriggerAnswerOptions(firstTrigger);
      applyRule(firstTrigger, answers[0]?.value ?? null);
    },
    [applyRule, priorTriggers],
  );

  const handleTriggerChange = useCallback(
    (linkId: string | null) => {
      if (!linkId) return;
      const trigger = priorTriggers.find((t) => t.linkId === linkId);
      if (!trigger) return;
      const answers = getTriggerAnswerOptions(trigger);
      const existing = rule?.triggerLinkId === linkId ? rule.answerKey : answers[0]?.value;
      applyRule(trigger, existing ?? answers[0]?.value ?? null);
    },
    [applyRule, priorTriggers, rule],
  );

  const handleAnswerChange = useCallback(
    (answerKey: string | null) => {
      if (!selectedTrigger || !answerKey) return;
      applyRule(selectedTrigger, answerKey);
    },
    [applyRule, selectedTrigger],
  );

  return (
    <Stack gap="sm" w={280}>
      <Text size="sm" fw={600}>
        Visibility
      </Text>
      <Radio.Group value={mode} onChange={handleModeChange}>
        <Stack gap={6}>
          <Radio value="always" label="Always visible" size="xs" />
          <Radio
            value="conditional"
            label="Show only when…"
            size="xs"
            disabled={priorTriggers.length === 0}
          />
        </Stack>
      </Radio.Group>
      {mode === 'conditional' && (
        <Stack gap="xs">
          <Select
            label="Question"
            size="xs"
            data={priorTriggers.map((t) => ({
              value: t.linkId!,
              label: t.text?.trim() || t.linkId!,
            }))}
            value={rule?.triggerLinkId ?? null}
            onChange={handleTriggerChange}
            styles={{ input: INNER_INPUT_BG }}
            allowDeselect={false}
          />
          <Select
            label="Answer"
            size="xs"
            data={answerOptions}
            value={rule?.answerKey ?? null}
            onChange={handleAnswerChange}
            styles={{ input: INNER_INPUT_BG }}
            allowDeselect={false}
            disabled={!selectedTrigger || answerOptions.length === 0}
          />
        </Stack>
      )}
      {priorTriggers.length === 0 && isConditionalSiblingItem(item) && (
        <Text size="xs" c="red">
          Trigger question is missing or invalid. Set visibility to Always or move this block below
          a single-choice or yes/no question.
        </Text>
      )}
      {priorTriggers.length === 0 && !isConditionalSiblingItem(item) && (
        <Text size="xs" c="dimmed">
          Add a single-choice or yes/no question above this one to enable conditional visibility.
        </Text>
      )}
    </Stack>
  );
}

/** Gear control (left of duplicate) opening a popover to edit enableWhen visibility. */
export function ConditionalVisibilityGear({
  item,
  siblings,
  siblingIndex,
  onUpdate,
}: ConditionalVisibilityGearProps): JSX.Element {
  const [opened, setOpened] = useState(false);
  const isConditional = isConditionalSiblingItem(item);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      width={300}
      shadow="md"
      withArrow
      clickOutsideEvents={['mousedown', 'touchstart']}
    >
      <Popover.Target>
        <Tooltip label="Visibility settings" withArrow position="top">
          <ActionIcon
            variant={isConditional ? 'light' : 'subtle'}
            color={isConditional ? 'gold' : 'gray'}
            onClick={(e) => {
              e.stopPropagation();
              setOpened((o) => !o);
            }}
          >
            <IconSettings size={14} />
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown onClick={(e) => e.stopPropagation()}>
        <VisibilitySettings
          item={item}
          siblings={siblings}
          siblingIndex={siblingIndex}
          onUpdate={onUpdate}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
